import React from 'react';
import {
  getPlatformURL,
  isStripeDomain,
} from '@stripe-internal/connect-embedded-lib';
import type {IAccountSessionComponents} from './AuthContext/AuthContext';
import {useAuthContext} from './AuthContext/AuthContext';
import type {ConnectElementConfigEnabledKey} from '../connect/ConnectJSInterface/ConnectElementList';
import {useConnectJsOptionsContext} from './ConnectJsOptionsContext/useConnectJsOptionsContext';
import {getStronglyTypedEntries} from '../utils/getStronglyTypedEntries';

type FeaturesContextContent = {
  availableFeatures: FeaturesMap | undefined;
};

type ValueOf<T> = T[keyof T];
type ExcludeDummyField<T> = Exclude<T, 'dummyFieldDoNotUse'>;
// features across all components
export type Feature = Exclude<
  ExcludeDummyField<
    ValueOf<{
      [K in keyof IAccountSessionComponents]: keyof IAccountSessionComponents[K]['features'];
    }>
  >,
  // We exclude allowed_apps, as that isn't a boolean
  'allowed_apps'
>;

// Including undefined, as features are not present in all components
type FeaturesMap = Record<Feature, boolean | undefined>;

export const FeaturesContext = React.createContext<FeaturesContextContent>({
  availableFeatures: undefined,
});
FeaturesContext.displayName = 'FeaturesContext';

export function useFeature(feature: Feature) {
  const {availableFeatures} = React.useContext(FeaturesContext);
  if (!availableFeatures) return false;
  return !!availableFeatures[feature];
}

export type Props = {
  component: ConnectElementConfigEnabledKey;
};

export const FeaturesProvider: React.FC<Props> = ({children, component}) => {
  // FeaturesProvider must be placed under AuthProvider
  const {authInfo} = useAuthContext();
  const {
    values: {metaOptions},
  } = useConnectJsOptionsContext();

  // assume features are off when loading
  if (!authInfo || component === 'internal') {
    return <>{children}</>;
  }

  const featureOverrides =
    metaOptions?.featureOverrides && metaOptions.featureOverrides[component]
      ? metaOptions.featureOverrides[component]
      : {};
  const runningInStripeDomain = isStripeDomain(new URL(getPlatformURL()));
  const featuresInAuthInfo = authInfo.components[component].features;

  // We directly flatten these features into a single typed object to avoid casting
  const availableFeatures: FeaturesMap = {
    capture_payments:
      'capture_payments' in featuresInAuthInfo
        ? featuresInAuthInfo.capture_payments
        : undefined,
    card_management:
      'card_management' in featuresInAuthInfo
        ? featuresInAuthInfo.card_management
        : undefined,
    card_spend_dispute_management:
      'card_spend_dispute_management' in featuresInAuthInfo
        ? featuresInAuthInfo.card_spend_dispute_management
        : undefined,
    cardholder_management:
      'cardholder_management' in featuresInAuthInfo
        ? featuresInAuthInfo.cardholder_management
        : undefined,
    destination_on_behalf_of_charge_management:
      'destination_on_behalf_of_charge_management' in featuresInAuthInfo
        ? featuresInAuthInfo.destination_on_behalf_of_charge_management
        : undefined,
    dispute_management:
      'dispute_management' in featuresInAuthInfo
        ? featuresInAuthInfo.dispute_management
        : undefined,
    edit_payout_schedule:
      'edit_payout_schedule' in featuresInAuthInfo
        ? featuresInAuthInfo.edit_payout_schedule
        : undefined,
    external_account_collection:
      'external_account_collection' in featuresInAuthInfo
        ? featuresInAuthInfo.external_account_collection
        : undefined,
    disable_stripe_user_authentication:
      'disable_stripe_user_authentication' in featuresInAuthInfo
        ? featuresInAuthInfo.disable_stripe_user_authentication ?? undefined
        : undefined,
    instant_payouts:
      'instant_payouts' in featuresInAuthInfo
        ? featuresInAuthInfo.instant_payouts
        : undefined,
    money_movement:
      'money_movement' in featuresInAuthInfo
        ? featuresInAuthInfo.money_movement
        : undefined,
    refund_management:
      'refund_management' in featuresInAuthInfo
        ? featuresInAuthInfo.refund_management
        : undefined,
    send_money:
      'send_money' in featuresInAuthInfo
        ? featuresInAuthInfo.send_money
        : undefined,
    spend_control_management:
      'spend_control_management' in featuresInAuthInfo
        ? featuresInAuthInfo.spend_control_management
        : undefined,
    standard_payouts:
      'standard_payouts' in featuresInAuthInfo
        ? featuresInAuthInfo.standard_payouts
        : undefined,
    topups:
      'topups' in featuresInAuthInfo ? !!featuresInAuthInfo.topups : undefined,
    transfer_balance:
      'transfer_balance' in featuresInAuthInfo
        ? featuresInAuthInfo.transfer_balance
        : undefined,
  };

  // Only allow feature overrides if in an internal Stripe domain
  if (runningInStripeDomain && featureOverrides) {
    getStronglyTypedEntries(featureOverrides).forEach(([feature, enabled]) => {
      if (enabled !== undefined) {
        availableFeatures[feature] = enabled;
      }
    });
  }

  return (
    <FeaturesContext.Provider
      value={{
        availableFeatures,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};
